export type Rect = {
    left    : number
    top     : number
    width   : number
    height  : number
}

export type ArrowAlignParams = {
    targetRect      : Rect
    containerRect   : Rect
    arrowSize       : number    // arrow element is square
    classes         : string    // tether element className, decides which axis the arrow moves on
    arrowTarget?    : string    // "<top|middle|bottom> <left|center|right>", default "middle center"
    arrowOffset?    : string    // "dy dx" px nudge applied to the resolved point
    edgeInset?      : EdgeInsetParams   // set → master-style arrow, takes precedence over arrowTarget
}

export type EdgeInsetParams = {
    inset           : number    // px from the popover's attached edge
    targetOffset?   : string    // tether targetOffset "y x"
    offset?         : string    // tether offset "y x"
}

export type ArrowAlignResult = {
    axis    : 'x' | 'y'
    value   : number    // px offset of the arrow div from the container's left (x) or top (y)
}

const FRACTIONS : DictionaryStringAny = {
    top     : 0,
    left    : 0,
    middle  : 0.5,
    center  : 0.5,
    bottom  : 1,
    right   : 1,
}

// direction the inset steps in from each attached edge
const INSET_SIGNS : DictionaryStringAny = {
    top     : 1,
    left    : 1,
    bottom  : -1,
    right   : -1,
}

// resolves "middle center" style attachment into a page point on the target
export function popoverArrow_resolveTargetPoint(rect: Rect, arrowTarget: string = 'middle center', arrowOffset: string = '0 0') {
    const [vert, horiz]         = arrowTarget.split(' ')
    const [offsetY, offsetX]    = arrowOffset.split(' ').map(Number)

    return {
        x   : rect.left + rect.width  * _.get(FRACTIONS, horiz, 0.5) + (offsetX || 0),
        y   : rect.top  + rect.height * _.get(FRACTIONS, vert,  0.5) + (offsetY || 0),
    }
}

function attachedSide(classes: string[], prefix: string, sides: string[], fallback: string) {
    return _.find(sides, (side) => {
        return classes.includes(prefix + side)
    }) || fallback
}

// tether offset value: px, or % of `size` (pass 0 to ignore % - element-relative offsets aren't resolved)
function offsetToPx(value: string | undefined, size: number) {
    if (!value) {
        return 0
    }
    if (_.endsWith(value, '%')) {
        return size * parseFloat(value) / 100
    }
    return parseFloat(value) || 0
}

// Master-style arrow point: where the old CSS arrow sat on a nominally placed popover - `inset` px in from
// the popover's attached edge (CSS used 20px for arrows v1, 26px for v2). Popover edge is reconstructed the
// way Tether places it: target attachment point + targetOffset - offset. Measured on the target, so when
// constraints shift the popover the arrow keeps pointing at the same spot of the anchor. Sides come from
// tether classes (not props) so flips are followed.
export function popoverArrow_edgeInsetPoint(rect: Rect, classes: string, params: EdgeInsetParams, arrowOffset: string = '0 0') {
    const list                      = classes.split(' ')
    const [arrowOffY, arrowOffX]    = arrowOffset.split(' ').map(Number)
    const [targetOffY, targetOffX]  = (params.targetOffset || '0 0').split(' ')
    const [offY, offX]              = (params.offset || '0 0').split(' ')

    const elementH  = attachedSide(list, 'tether-element-attached-', ['left', 'right'], 'center')
    const elementV  = attachedSide(list, 'tether-element-attached-', ['top', 'bottom'], 'middle')
    const targetH   = attachedSide(list, 'tether-target-attached-',  ['left', 'center', 'right'], 'center')
    const targetV   = attachedSide(list, 'tether-target-attached-',  ['top', 'middle', 'bottom'], 'middle')

    const x = rect.left + rect.width  * FRACTIONS[targetH] + offsetToPx(targetOffX, rect.width)  - offsetToPx(offX, 0)
    const y = rect.top  + rect.height * FRACTIONS[targetV] + offsetToPx(targetOffY, rect.height) - offsetToPx(offY, 0)

    return {
        x   : x + params.inset * _.get(INSET_SIGNS, elementH, 0) + (arrowOffX || 0),
        y   : y + params.inset * _.get(INSET_SIGNS, elementV, 0) + (arrowOffY || 0),
    }
}

// element-top touching target-bottom (or vice-versa) means popover is above/below target → arrow moves on x
export function popoverArrow_isHorizontal(classes: string) {
    return (classes.includes('tether-element-attached-top')    && classes.includes('tether-target-attached-bottom'))
        || (classes.includes('tether-element-attached-bottom') && classes.includes('tether-target-attached-top'))
}

// Tether constraints (pin to window, etc.) can push the popover on top of its anchor - an arrow there
// points at nothing, so it gets hidden. 1px tolerance for sub-pixel rounding of edge-to-edge attachment.
export function popoverArrow_overlapsTarget(targetRect: Rect, containerRect: Rect) {
    const tolerance = 1

    return containerRect.left + tolerance < targetRect.left + targetRect.width
        && targetRect.left + tolerance    < containerRect.left + containerRect.width
        && containerRect.top + tolerance  < targetRect.top + targetRect.height
        && targetRect.top + tolerance     < containerRect.top + containerRect.height
}

// Returns where the arrow div should sit inside the container so its TIP lands on the target point.
// Subtracts half the arrow size (mirrors CSS mixin: top: calc($top - $arrowSize/2)).
// Clamped to the container so a Tether-pinned popover that no longer overlaps the anchor still shows the
// arrow, as close to the anchor as the geometry allows - it's rotated 45deg so keep one arrow size clear
// of the corners/border radius.
export function popoverArrow_align(params: ArrowAlignParams): ArrowAlignResult {
    const point     = params.edgeInset
        ? popoverArrow_edgeInsetPoint(params.targetRect, params.classes, params.edgeInset, params.arrowOffset)
        : popoverArrow_resolveTargetPoint(params.targetRect, params.arrowTarget, params.arrowOffset)
    const half      = params.arrowSize / 2
    const axis      = popoverArrow_isHorizontal(params.classes) ? 'x' : 'y'

    const raw       = axis === 'x'
        ? point.x - params.containerRect.left - half
        : point.y - params.containerRect.top  - half
    const extent    = axis === 'x' ? params.containerRect.width : params.containerRect.height

    const min       = params.arrowSize
    const max       = extent - params.arrowSize * 2

    return {
        axis    : axis,
        value   : max < min ? raw : _.clamp(raw, min, max),
    }
}

// When Tether pins the popover to a window edge it passes attachment.left/top = false on the pinned axis to
// updateAttachClasses, which strips the attached-left/right (or top/bottom) classes - then no arrow CSS rule
// matches and the arrow disappears. This happens on every position() call (scroll/resize included, even when
// nothing moves and no 'repositioned' fires), so patch the instance method to fall back to the nominal
// attachment instead of restoring the classes after the fact.
export function popoverArrow_keepAttachClassesWhenPinned(tether: any) {
    const original = tether.updateAttachClasses

    tether.updateAttachClasses = (elementAttach?: any, targetAttach?: any) => {
        const e = elementAttach || tether.attachment
        const t = targetAttach  || tether.targetAttachment

        return original.call(tether, {
            top     : e.top  || tether.attachment.top,
            left    : e.left || tether.attachment.left,
        }, {
            top     : t.top  || tether.targetAttachment.top,
            left    : t.left || tether.targetAttachment.left,
        })
    }

    return tether
}
